import {Station} from "../../stations/station.service.interface";
import {IServiceFactory} from "../../service-factory.interface";
import {makeObservable, observable, runInAction} from "mobx";
import {Check} from "../../../types/type-checking";
import {MonthModel} from "../../time/month.model";
import {ILowFareResult, IOneWayLowFareReader, LowFareStatusEnum} from "./low-fare-reader.interface";
import {NullableNumber, NullableString, NullableUndefinedNumber} from "../../../types/nullable-types";
import {IFlightScheduleFilter} from "../../flight-search/flights-schedule/flight-schedule-filter.interface";
import {ILowFaresCache} from "../low-fare.service.interface";
import {sortJSON} from "../../../utils/sort-json";
import {ILargeCacheHandlerEventSubscription} from "../../storage/large-cache.handler.interface";
import {TimeSpan} from "../../../types/time-span";
import {IDotRezUserSession} from "../../dot-rez-api/session/user-session/dot-rez-user.session.interface";
import {
    IDotRezAvailabilityLowFarePassengers,
    IDotRezAvailabilityLowFareRequest
} from "../../dot-rez-api/data-contracts/requests/lowfare-search.request";
import {
    IDotRezAvailabilityLowFareResponseV3
} from "../../dot-rez-api/data-contracts/responses/lowfare-search.response";


export class OneWayLowFareReader implements IOneWayLowFareReader {
    constructor(private readonly originStation: Station,
                private readonly destinationStation: Station,
                private readonly lowFareCache: ILowFaresCache,
                private readonly getDotRezSession: () => Promise<IDotRezUserSession>,
                private readonly getClassesOfService: () => Promise<string[]>,
                private readonly getAnonymousAgentName: () => NullableString,
                private readonly getMinPriceAmount: () => NullableNumber,
                public readonly getCurrency: () => string,
                private readonly scheduleFilter: IFlightScheduleFilter,
                private readonly services: IServiceFactory,
                private readonly passengersCount: () => IDotRezAvailabilityLowFarePassengers) {

        makeObservable<this, '_monthlyLowFares'>(this, {
           _monthlyLowFares: observable
        });
    }

    private _monthlyLowFares: Record<string, MonthLowFaresReader> = {};

    private _getMonthLowFaresReader(date: Date) {
        const month = new MonthModel(date.getMonth(), date.getFullYear(), this.services.time);
        if(!this._monthlyLowFares[month.key]) {
            this._monthlyLowFares[month.key] = new MonthLowFaresReader(month,
                this.originStation,
                this.destinationStation,
                this.lowFareCache,
                this.getDotRezSession,
                this.getClassesOfService,
                this.getAnonymousAgentName,
                this.getMinPriceAmount,
                this.getCurrency,
                this.scheduleFilter,
                this.services,
                this.passengersCount(),
                this._onMonthRemovedFromCache);
        }

        return this._monthlyLowFares[month.key];
    }

    private _onMonthRemovedFromCache= (month: MonthModel) => {
        if(this._monthlyLowFares[month.key]) {
            runInAction(() => {
                this._monthlyLowFares[month.key].dispose();
                delete this._monthlyLowFares[month.key];
            });
        }
    }

    getLowFare(date: Date): ILowFareResult {
        return this._getMonthLowFaresReader(date).getLowFare(date);
    }

    getLowFareAsync(date: Date): Promise<ILowFareResult> {
        return this._getMonthLowFaresReader(date).getLowFareAsync(date);
    }

    dispose() {
        Object.values(this._monthlyLowFares).forEach(mlf => {
            mlf.dispose();
        })

        runInAction(() => {
            this._monthlyLowFares = {};
        });
    }
}


class MonthLowFaresReader {
    constructor(private readonly month: MonthModel,
                private readonly originStation: Station,
                private readonly destinationStation: Station,
                private readonly lowFareCache: ILowFaresCache,
                private readonly getDotRezSession: () => Promise<IDotRezUserSession>,
                private readonly getClassesOfServices: () => Promise<string[]>,
                private readonly getAnonymousAgentName: () => NullableString,
                private readonly getMinPriceAmount: () => NullableNumber,
                private readonly getCurrency: () => string,
                private readonly scheduleFilter: IFlightScheduleFilter,
                private readonly services: IServiceFactory,
                private readonly passengersCount: IDotRezAvailabilityLowFarePassengers,
                private readonly onMonthRemovedFromCache: (month: MonthModel) => void) {


        makeObservable<this, '_cacheHash' >(this, {
            _cacheHash: observable.ref
        });

        this._onCacheItemRemovedSubscription = lowFareCache.onCacheItemRemoved(this._onCacheItemRemovedHandler);

        this._loadLowFaresPromise = this._loadLowFares();
    }

    private _cacheHash: NullableString = null;

    private readonly _onCacheItemRemovedSubscription: ILargeCacheHandlerEventSubscription;

    private _onCacheItemRemovedHandler = (hash: string) => {
        if(hash === this._cacheHash) {
            this.onMonthRemovedFromCache(this.month);
        }
    }

    dispose(): void {
        this._onCacheItemRemovedSubscription.unsubscribe();
    }

    getLowFare(date: Date): ILowFareResult {

        if(date < this.services.time.makeShortDate(this.services.time.currentDate)) {
            return {
                status: LowFareStatusEnum.NoFare
            };
        }

        if(this.scheduleFilter.shouldFilterOutDate(date)) {
            return {
                status: LowFareStatusEnum.NoFare
            };
        }

        if(!this._cacheHash) {
            return {
                status: LowFareStatusEnum.Reading
            };
        }

        const dayLowFareCache = this.lowFareCache.getFromCache(this._cacheHash, this._formatDate(date));

        if(!dayLowFareCache) {
            return {
                status: LowFareStatusEnum.NoFare
            };
        }

        if(dayLowFareCache.isCacheExpired) {
            runInAction(() => {
                this._cacheHash = null;
            });

            this._loadLowFaresPromise = this._loadLowFares();
            return {
                status: LowFareStatusEnum.Reading
            };
        }

        if(!dayLowFareCache.dayLowFare) {
            return {
                status: LowFareStatusEnum.NoFare
            };
        }

        if(!dayLowFareCache.dayLowFare.price) {
            return dayLowFareCache.dayLowFare;
        }

        const minPrice = this.getMinPriceAmount();
        if(Check.isNullOrUndefined(minPrice)) {
            return dayLowFareCache.dayLowFare;
        }

        if(dayLowFareCache.dayLowFare.price.amount < minPrice) {
            return {
                status: LowFareStatusEnum.NoFare
            };
        }

        return dayLowFareCache.dayLowFare;
    }

    private _loadLowFaresPromise: Promise<void>;



    async getLowFareAsync(date: Date): Promise<ILowFareResult> {
        await this._loadLowFaresPromise;
        return this.getLowFare(date);
    }

    private async _getSession(): Promise<IDotRezUserSession> {
        return await this.getDotRezSession();
    }

    private _formatDate(date: Date | string): string {
        return this.services.time.formatYYYY_MM_DD(date);
    }

    private async _computeRequestHash(lowFareRequest: IDotRezAvailabilityLowFareRequest): Promise<string> {
        const agentName = this.getAnonymousAgentName() ?? "";
        return await this.services.crypto.computeHash(JSON.stringify(sortJSON(lowFareRequest)) + agentName);
    }


    private async _loadLowFares(): Promise<void> {
        let requestHash: string = "";
        try {
            runInAction(() => {
                this._cacheHash = null;
            });

            const lowFareRequest = await this._createLowFareRequest();
            requestHash = await this._computeRequestHash(lowFareRequest);

            if(!this.lowFareCache.isCacheExpired(requestHash)) {
                runInAction(() => {
                      this._cacheHash = requestHash;
                })
                return;
            }

            const lowFaresAvailability = await this._getLowFaresAvailability(lowFareRequest);

            runInAction(() => {
                this._forEachDayInMonth(formattedDate => {
                    this.lowFareCache.putInCache(requestHash, formattedDate, this._createLowFareResult(lowFaresAvailability[formattedDate]));
                });

                this._cacheHash = requestHash;
            });
        } catch (err) {
            this.services.logger.error(`Failed to read low fares for ${this.originStation.stationMacCode} | ${this.destinationStation.stationMacCode} | ${this.month.key}`, err);
            requestHash = requestHash || `error-${this.originStation.stationMacCode}-${this.destinationStation.stationMacCode}-${this.month.key}}`;
            runInAction(() => {

                this._forEachDayInMonth(formattedDate => {
                    this.lowFareCache.putInCache(requestHash, formattedDate, {status: LowFareStatusEnum.Error});
                });

                this.lowFareCache.setCacheExpirationTime(requestHash, TimeSpan.fromSeconds(5));

                this._cacheHash = requestHash;
            });
        }
    }

    private async _getLowFaresAvailability(request: IDotRezAvailabilityLowFareRequest): Promise<Record<string, NullableUndefinedNumber>> {
        const session = await this._getSession();
        const lowFares = await session.availabilityLowFare(request);
        return this._getLowFaresV3(lowFares as IDotRezAvailabilityLowFareResponseV3);

    }


    private _getLowFaresV3(apiResponse: IDotRezAvailabilityLowFareResponseV3): Record<string, NullableUndefinedNumber> {
        const trips = apiResponse.trips ?? [];
        if(trips.length === 0) {
            return {};
        }


        let lowFareDateMarkets = trips[0].lowFareAvailableByMarket[`${this.originStation.stationMacCode}|${this.destinationStation.stationMacCode}`];

        const lowFaresAvailability: Record<string, NullableUndefinedNumber> = {};

        for(let record of lowFareDateMarkets) {
            const key = this._formatDate(record.departureDate);
            const oldVal = lowFaresAvailability[key];
            const newVal = record.lowestFareAmount?.fareAmount;

            if(Check.isNullOrUndefined(oldVal)) {
                lowFaresAvailability[key] = newVal;
            } else if(!Check.isNullOrUndefined(newVal)) {
                lowFaresAvailability[key] = Math.min(oldVal, newVal);
            }
        }

        return lowFaresAvailability;
    }

    private _createLowFareResult(amount: NullableUndefinedNumber): ILowFareResult {
        if(Check.isNullOrUndefined(amount)) {
            return {
                status: LowFareStatusEnum.NoFare
            }
        }

        return {
            status: LowFareStatusEnum.Loaded,
            price: this.services.currency.createPrice(amount, this.getCurrency())
        };
    }

    private async _createLowFareRequest() : Promise<IDotRezAvailabilityLowFareRequest> {
        const time = this.services.time;
        const numberOfDays = time.differenceInCalendarDays(this.month.firstDate, this.month.lastDate);

        let daysToLeft = Math.round(numberOfDays / 2)
        let daysToRight = numberOfDays - daysToLeft;


        const classesOfService = await this.getClassesOfServices();

        return {
            origin: this.originStation.stationMacCode,
            destination: this.destinationStation.stationMacCode,
            beginDate: time.formatYYYY_MM_DD(this.month.firstDate),
            endDate: time.formatYYYY_MM_DD(this.month.lastDate),
            middleDate: time.formatYYYY_MM_DD(time.addDays(this.month.firstDate, daysToLeft)),
            currencyCode: this.getCurrency(),
            loyaltyFilter: 'MonetaryOnly',
            daysToLeft: daysToLeft,
            daysToRight: daysToRight,
            passengers: this.passengersCount,
            classesOfService: classesOfService
        };
    }

    private _forEachDayInMonth(callback: (formattedDate: string) => void): void {
        for(let date = this.month.firstDate; date <= this.month.lastDate; date = this.services.time.addDays(date, 1)) {
            callback(this._formatDate(date));
        }
    }
}

